import { useEffect, useState } from 'react';

import type { Id, QltSlotAttendance, QltViolation } from '../../api/types';
import { Modal } from '../../components';
import { REASON_PRESETS } from '../../config/violation-reasons';
import { classLabel } from '../periodLabels';
import { composeDescription } from './conductEntry';
import '../../styles/class-management.css';

const NOTE_LIMIT = 200;
const WEEKDAYS = ['Chủ nhật', 'Thứ 2', 'Thứ 3', 'Thứ 4', 'Thứ 5', 'Thứ 6', 'Thứ 7'];

function slotSubtitle(classTitle: string, date: string): string {
  const day = date.slice(0, 10);
  const parts = day.split('-');
  return `${classLabel(classTitle)} - ${WEEKDAYS[new Date(`${day}T00:00:00Z`).getUTCDay()] || ''}, ${parts[2]}/${parts[1]}/${parts[0]}`;
}

export interface ViolationFormValue {
  student: Id;
  type: QltViolation['type'];
  description: string;
}

export interface ViolationFormProps {
  open: boolean;
  classTitle: string;
  date: string;
  initialType: QltViolation['type'];
  students: QltSlotAttendance[];
  saving: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (value: ViolationFormValue) => void;
}

function TypeOption({ label, active, tone, onSelect }: { label: string; active: boolean; tone: QltViolation['type']; onSelect: () => void }) {
  return (
    <button type="button" role="radio" aria-checked={active} className={`cm-type cm-type--${tone}${active ? ' is-on' : ''}`} onClick={onSelect}>
      <span className="cm-type__box" aria-hidden={true}>✓</span>{label}
    </button>
  );
}

export function ViolationForm({ open, classTitle, date, initialType, students, saving, error, onClose, onSubmit }: ViolationFormProps) {
  const [studentId, setStudentId] = useState('');
  const [type, setType] = useState<QltViolation['type']>(initialType);
  const [reasons, setReasons] = useState<string[]>([]);
  const [note, setNote] = useState('');

  useEffect(() => {
    if (!open) return;
    setStudentId(students.length > 0 ? String(students[0].student._id || students[0].student.id || '') : '');
    setType(initialType);
    setReasons([]);
    setNote('');
  }, [open, initialType, students]);

  function switchType(next: QltViolation['type']) {
    setType(next);
    setReasons([]);
  }

  function toggleReason(reason: string) {
    setReasons((current) => (current.indexOf(reason) >= 0 ? current.filter((item) => item !== reason) : current.concat(reason)));
  }

  // one record per submission - the rail list is one line per submission, fanning out would misreport the count
  function submit() {
    onSubmit({ student: studentId, type, description: composeDescription(REASON_PRESETS[type], reasons, note) });
  }

  const contentLabel = type === 'commendation' ? 'Nội dung khen thưởng' : 'Nội dung Vi phạm';
  const actions = <button type="button" className="cm-btn-primary" disabled={saving || !studentId || reasons.length === 0} onClick={submit}>{saving ? 'Đang lưu…' : 'Xác nhận ghi sổ'}</button>;

  return (
    <Modal open={open} title="Khen thưởng - Kỉ luật" description={slotSubtitle(classTitle, date)} className="cm-modal" actions={actions} onClose={onClose}>
      <label className="cm-label" htmlFor="violation-student">Học sinh</label>
      <select id="violation-student" className="cm-select" value={studentId} onChange={(event) => setStudentId(event.target.value)}>
        {students.length === 0 ? <option value="">Chưa có danh sách học sinh</option> : students.map((row) => <option key={row.student._id} value={String(row.student._id || row.student.id)}>{row.student.name || row.student.code || 'Học sinh'}</option>)}
      </select>
      <div className="cm-type-toggle" role="radiogroup" aria-label="Loại ghi nhận">
        <TypeOption label="Khen thưởng" tone="commendation" active={type === 'commendation'} onSelect={() => switchType('commendation')} />
        <TypeOption label="Vi phạm kỉ luật" tone="violation" active={type === 'violation'} onSelect={() => switchType('violation')} />
      </div>
      <div className="cm-label">{contentLabel}</div>
      <div className="cm-checklist" role="group" aria-label={contentLabel}>
        {REASON_PRESETS[type].map((reason) => (
          <label className={reasons.indexOf(reason) >= 0 ? 'cm-check is-on' : 'cm-check'} key={reason}>
            <input type="checkbox" checked={reasons.indexOf(reason) >= 0} onChange={() => toggleReason(reason)} />
            <span>{reason}</span>
          </label>
        ))}
      </div>
      <div className="cm-label">Ghi chú thêm</div>
      <div className="cm-note">
        <textarea value={note} maxLength={NOTE_LIMIT} onChange={(event) => setNote(event.target.value)} placeholder="Nhập nội dung ghi chú..." aria-label="Ghi chú thêm" />
        <span className="cm-note__count">{note.length}/{NOTE_LIMIT}</span>
      </div>
      {error ? <p className="cm-error" role="alert">{error}</p> : null}
    </Modal>
  );
}
